// Dinamik biniş QR'ı ve PIN'i (D16, P-biniş kartı, okul QR'ı).
// Yolcuya özel anahtar sunucuda türetilir: key = HMAC(BOARDING_TOKEN_SECRET, "board:" + passengerId).
// Yolcu uygulaması anahtarı bir kez alır ve kodu çevrimdışı da üretir (TOTP benzeri, 30 sn QR / 60 sn PIN).
// Şoför uygulaması kodu sunucuya doğrulatır; çevrimdışıysa olay kuyruğa ham kodla girer, eşitlemede doğrulanır.
import { hmac } from '@noble/hashes/hmac';
import { sha256 } from '@noble/hashes/sha2';

export const BOARDING_QR_WINDOW_S = 30;
export const BOARDING_PIN_WINDOW_S = 60;
const QR_PREFIX = 'SRB1';
const enc = new TextEncoder();

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

export function toBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2]! + B64[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    if (b !== undefined) out += B64[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    if (c !== undefined) out += B64[c & 63]!;
  }
  return out;
}

export function fromBase64Url(s: string): Uint8Array {
  const clean = s.replace(/[^A-Za-z0-9\-_]/g, '');
  const out: number[] = [];
  let buf = 0;
  let bits = 0;
  for (const ch of clean) {
    buf = (buf << 6) | B64.indexOf(ch);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buf >> bits) & 255);
    }
  }
  return Uint8Array.from(out);
}

function mac(key: Uint8Array, msg: string): Uint8Array {
  return hmac(sha256, key, enc.encode(msg));
}

function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return d === 0;
}

/** Yalnız sunucu: yolcuya özel biniş anahtarı (base64url). */
export function deriveBoardingKey(secret: string, passengerId: string): string {
  return toBase64Url(mac(enc.encode(secret), `board:${passengerId}`));
}

function qrSig(keyB64: string, passengerId: string, window: number): string {
  return toBase64Url(mac(fromBase64Url(keyB64), `qr.${passengerId}.${window}`).slice(0, 12));
}

/** QR içeriği: SRB1.<yolcu>.<pencere>.<imza> — 30 sn'de bir döner. */
export function boardingQr(passengerId: string, keyB64: string, now = Date.now()): string {
  const w = Math.floor(now / 1000 / BOARDING_QR_WINDOW_S);
  return `${QR_PREFIX}.${passengerId}.${w}.${qrSig(keyB64, passengerId, w)}`;
}

/** 6 haneli PIN — 60 sn'de bir döner (dinamik kesme, RFC 4226 benzeri). */
export function boardingPin(keyB64: string, now = Date.now()): string {
  return pinFor(keyB64, Math.floor(now / 1000 / BOARDING_PIN_WINDOW_S));
}

function pinFor(keyB64: string, window: number): string {
  const h = mac(fromBase64Url(keyB64), `pin.${window}`);
  const o = h[h.length - 1]! & 15;
  const bin = ((h[o]! & 0x7f) << 24) | (h[o + 1]! << 16) | (h[o + 2]! << 8) | h[o + 3]!;
  return String(bin % 1_000_000).padStart(6, '0');
}

/** QR'dan kimlik kısmı (imza doğrulamadan) — çevrimdışı şoför listesinde eşleştirme için. */
export function parseBoardingQr(payload: string): { passengerId: string; window: number; sig: string } | null {
  const parts = payload.trim().split('.');
  if (parts.length !== 4 || parts[0] !== QR_PREFIX) return null;
  const window = Number(parts[2]);
  if (!parts[1] || !Number.isInteger(window) || !parts[3]) return null;
  return { passengerId: parts[1], window, sig: parts[3] };
}

/** QR doğrulama: ±skew pencere tolerans (saat kayması ve tarama gecikmesi). */
export function verifyBoardingQr(payload: string, keyB64: string, now = Date.now(), skew = 1): boolean {
  const p = parseBoardingQr(payload);
  if (!p) return false;
  const w = Math.floor(now / 1000 / BOARDING_QR_WINDOW_S);
  if (Math.abs(p.window - w) > skew) return false;
  return safeEqual(p.sig, qrSig(keyB64, p.passengerId, p.window));
}

export function verifyBoardingPin(pin: string, keyB64: string, now = Date.now(), skew = 1): boolean {
  if (!/^\d{6}$/.test(pin)) return false;
  const w = Math.floor(now / 1000 / BOARDING_PIN_WINDOW_S);
  for (let d = -skew; d <= skew; d++) if (safeEqual(pin, pinFor(keyB64, w + d))) return true;
  return false;
}
